// Calculs de placement dans le temps d'un atelier : prochain jour ouvert, et
// découpage d'une durée longue en journées. Fonctions PURES — aucune lecture
// d'état, aucun appel réseau : l'écran leur passe les horaires de l'atelier et
// une fonction qui dit si un jour est fermé (férié, absence du mécanicien…).
// Partagées par « Reporter au lendemain » et par le bouton « Répartir » de la
// modale d'intervention.

import { toYmd, ajouterJours, dateLocale } from './planningTime';

// Défauts de la table `planning_ateliers` (colonnes DEFAULT) : 07:00–17:00, lundi→samedi.
const OUVERTURE_DEFAUT = '07:00';
const FERMETURE_DEFAUT = '17:00';
const JOURS_DEFAUT = [1, 2, 3, 4, 5, 6];

const quart = (h) => Math.round(h * 4) / 4;
const quartInferieur = (h) => Math.floor(h * 4 + 1e-9) / 4;

export const hhmmEnMin = (t) => {
  if (!t) return null;
  const [h, m] = String(t).slice(0, 5).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : null;
};
export const minEnHhmm = (min) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

// Horaires d'un atelier, défauts compris. Sans atelier connu (mécanicien non
// rattaché), on prend l'ouverture la plus tôt, la fermeture la plus tard et
// l'union des jours des ateliers de repli fournis.
export const horairesAtelier = (atelier, replis = []) => {
  if (atelier) {
    return {
      ouverture: String(atelier.heure_ouverture || OUVERTURE_DEFAUT).slice(0, 5),
      fermeture: String(atelier.heure_fermeture || FERMETURE_DEFAUT).slice(0, 5),
      jours: (atelier.jours_ouverture && atelier.jours_ouverture.length)
        ? atelier.jours_ouverture.map(Number) : JOURS_DEFAUT,
      atelierId: atelier.id,
    };
  }
  const actifs = (replis || []).filter(a => a && a.actif !== false);
  if (!actifs.length) return { ouverture: OUVERTURE_DEFAUT, fermeture: FERMETURE_DEFAUT, jours: JOURS_DEFAUT, atelierId: null };
  const ouv = Math.min(...actifs.map(a => hhmmEnMin(a.heure_ouverture) ?? hhmmEnMin(OUVERTURE_DEFAUT)));
  const ferm = Math.max(...actifs.map(a => hhmmEnMin(a.heure_fermeture) ?? hhmmEnMin(FERMETURE_DEFAUT)));
  const jours = [...new Set(actifs.flatMap(a => (a.jours_ouverture && a.jours_ouverture.length)
    ? a.jours_ouverture.map(Number) : JOURS_DEFAUT))];
  return { ouverture: minEnHhmm(ouv), fermeture: minEnHhmm(ferm), jours, atelierId: null };
};

// Premier jour ouvert STRICTEMENT après `depuis` : un jour d'ouverture de
// l'atelier (ISO : lundi = 1 … dimanche = 7) que `estFerme(ymd)` ne ferme pas.
// Borné : un atelier fermé toute la semaine ferait sinon boucler l'écran.
export const prochainJourOuvert = (depuis, { jours = JOURS_DEFAUT, estFerme = () => false, maxEssais = 60 } = {}) => {
  let d = dateLocale(depuis);
  if (!d) return null;
  for (let i = 0; i < maxEssais; i++) {
    d = ajouterJours(d, 1);
    const iso = d.getDay() === 0 ? 7 : d.getDay();
    if (jours.includes(iso) && !estFerme(toYmd(d))) return d;
  }
  return null;
};

// Découpe `duree` (heures) en journées de travail sur un même mécanicien.
//   - 1er jour : de `heureDebut` à la fermeture, pauses déduites ;
//   - jours suivants : journées pleines, de l'ouverture à la fermeture, pauses
//     déduites, sur les seuls jours ouverts ;
//   - dernier jour : le reliquat.
// `pausesDuJour(ymd, debutMin, finMin)` renvoie les minutes de pause comprises
// entre les deux bornes ce jour-là (0 par défaut). Aucune vérification des
// autres dossiers du mécanicien : le chevauchement est accepté, comme pour le
// report au lendemain.
// Renvoie { segments: [{ date, heure, duree }], reste } — `reste` > 0 seulement
// si aucun jour ouvert n'a été trouvé pour tout placer.
export const repartirDuree = ({ date, heureDebut, duree, horaires, estFerme = () => false, pausesDuJour = () => 0, maxJours = 60 }) => {
  const total = quart(parseFloat(duree) || 0);
  const ouv = hhmmEnMin(horaires.ouverture);
  const ferm = hhmmEnMin(horaires.fermeture);
  const debut1 = hhmmEnMin(heureDebut) ?? ouv;
  const segments = [];
  if (total <= 0 || ferm <= ouv) return { segments: [{ date: toYmd(date), heure: heureDebut, duree: total }], reste: 0 };

  const capacite = (ymd, deMin) =>
    quartInferieur(Math.max(0, (ferm - deMin) - (pausesDuJour(ymd, deMin, ferm) || 0)) / 60);

  let reste = total;
  // Le jour du dossier garde au moins un quart d'heure : un dossier posé après
  // la fermeture reste visible là où on l'a mis.
  const jour1 = toYmd(date);
  const part1 = Math.max(0.25, Math.min(reste, capacite(jour1, debut1)));
  segments.push({ date: jour1, heure: String(heureDebut).slice(0, 5), duree: part1 });
  reste = quart(reste - part1);

  let d = date;
  for (let essais = 0; reste > 0 && segments.length < maxJours && essais < maxJours * 2; essais++) {
    d = prochainJourOuvert(d, { jours: horaires.jours, estFerme });
    if (!d) break;
    const ymd = toYmd(d);
    const cap = capacite(ymd, ouv);
    if (cap <= 0) continue;
    const part = Math.min(reste, cap);
    segments.push({ date: ymd, heure: minEnHhmm(ouv), duree: part });
    reste = quart(reste - part);
  }
  return { segments, reste };
};
